// src/components/auth/jobs/results/JobResults.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Button,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { JobService, OpenAPI } from '../../../../api';
import JobResultComments from './JobResult-Comments';
import JobResultStatistics from './JobResult-Statistics';

export default function JobResults({ jobId: propJobId }: { jobId?: string }) {
  const { id: routeJobId } = useParams<{ id?: string }>();
  const jobId = propJobId || routeJobId || '';

  const [status, setStatus] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [averageScore, setAverageScore] = useState<number | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  // Poll / WS kontroll
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef(status);

  // PDF-hez
  const reportRef = useRef<HTMLDivElement | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Csak a PDF-ben használt téma (NINCS CssBaseline, hogy ne legyen globális hatás)
  const reportTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: 'light' },
        typography: {
          fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
          h4: { fontWeight: 700, letterSpacing: 0.2 },
          h6: { fontWeight: 700, letterSpacing: 0.2 },
          body1: { lineHeight: 1.55 },
        },
        shape: { borderRadius: 16 },
      }),
    []
  );

  // ref szinkron + Completed/Failed esetén polling stop
  useEffect(() => {
    statusRef.current = status;
    if ((status === 'Completed' || status === 'Failed') && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [status]);

  useEffect(() => {
    if (!jobId || isNaN(Number(jobId))) {
      console.error('Érvénytelen jobId:', jobId);
      return;
    }

    const ws = new WebSocket(`${OpenAPI.WS_BASE}/ws?jobId=${jobId}`);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setStatus(data.status);
        if (data.status === 'Completed' || data.status === 'Failed') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          fetchData();
        }
      } catch (error) {
        console.error('WebSocket error:', error);
      }
    };

    ws.onerror = (e) => {
      console.warn('WebSocket hiba:', e);
    };

    // Induló lekérés + polling (ref-et figyelünk, nem a zárt status-t)
    fetchData();
    intervalRef.current = setInterval(() => {
      if (statusRef.current !== 'Completed' && statusRef.current !== 'Failed') {
        fetchData();
      }
    }, 1200);

    return () => {
      try {
        ws.close();
      } catch {}
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [commentsData, averageData] = await Promise.all([
        JobService.getApiJobComments(Number(jobId)),
        JobService.getApiJobAverageScore(Number(jobId)),
      ]);
      setComments(commentsData);
      setAverageScore(averageData?.averageScore ?? null);
      setLoading(false);
    } catch (error) {
      console.error('Hiba az adatok frissítésekor:', error);
    }
  };

  const handleTabChange = (_e: React.SyntheticEvent, v: number) => setTabIndex(v);

  // Státusz chip szín
  const chipColor =
    status === 'Completed' ? 'success' :
    status === 'Pending'   ? 'warning' :
    status === 'Failed'    ? 'error'   :
    'info'; // Queued/Unknown -> info

  /**
   * Rejtett riport CSAK a PDF-hez – saját (lokális) stílusokkal.
   * Nincs CssBaseline. Az on-screen UI-hoz nem nyúl.
   */
  const ReportContent = useMemo(
    () => (
      <ThemeProvider theme={reportTheme}>
        {/* Fehér háttér, fix 1080 szélesség a chartokhoz */}
        <Box
          sx={{
            width: 1080,
            bgcolor: '#ffffff',
            color: 'text.primary',
            p: 6,
            fontFamily:
              'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
          }}
        >
          {/* Fejléc */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ mb: 0.5 }}>
                Elemzés riport #{jobId}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Állapot: {status || 'N/A'} • {averageScore !== null ? `Átlag: ${averageScore.toFixed(2)}` : 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={status || 'N/A'}
                color={chipColor as any}
                size="small"
              />
              {averageScore !== null && (
                <Chip label={`Átlag: ${averageScore.toFixed(2)}`} color="primary" variant="outlined" size="small" />
              )}
            </Box>
          </Box>

          {/* Szép “kártyák” csak a PDF-ben (Box-szal) */}
          <Box
            sx={{
              mb: 4,
              p: 3,
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              backgroundColor: '#fff',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Kommentek
            </Typography>
            <Box sx={{ height: 1, bgcolor: '#e5e7eb', mb: 2 }} />
            <JobResultComments comments={comments} status={status} />
          </Box>

          <Box
            sx={{
              p: 3,
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              backgroundColor: '#fff',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Statisztikák
            </Typography>
            <Box sx={{ height: 1, bgcolor: '#e5e7eb', mb: 2 }} />
            {/* Chartok 1080px szélességű szülőben → stabil méret a képen */}
            <JobResultStatistics comments={comments} />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              Generálva: {new Date().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    ),
    [reportTheme, jobId, status, averageScore, comments]
  );

  const handleDownloadPdf = async () => {
    if (!reportRef.current || pdfGenerating) return;
    setPdfGenerating(true);
    try {
      // várunk 2 frame-et, hogy a chartok biztosan kész legyenek
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const scale = Math.max(2, (window.devicePixelRatio || 1.5));

      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        useCORS: true,
        scale,
        windowWidth: 1080,
      } as any);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidthMM = pdf.internal.pageSize.getWidth();
      const pageHeightMM = pdf.internal.pageSize.getHeight();

      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;

      const pagePixelHeight = Math.floor(imgWidthPx * (pageHeightMM / pageWidthMM));
      const pages: { dataUrl: string; hMM: number }[] = [];

      for (let y = 0; y < imgHeightPx; y += pagePixelHeight) {
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidthPx;
        sliceCanvas.height = Math.min(pagePixelHeight, imgHeightPx - y);
        const ctx = sliceCanvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          y,
          imgWidthPx,
          sliceCanvas.height,
          0,
          0,
          imgWidthPx,
          sliceCanvas.height
        );
        pages.push({
          dataUrl: sliceCanvas.toDataURL('image/png'),
          hMM: (sliceCanvas.height / imgWidthPx) * pageWidthMM,
        });
      }

      pages.forEach((p, i) => {
        if (i > 0) pdf.addPage();
        pdf.addImage(p.dataUrl, 'PNG', 0, 0, pageWidthMM, p.hMM);
        // oldalszám
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`${i + 1} / ${pages.length}`, pageWidthMM - 10, pageHeightMM - 6, { align: 'right' });
      });

      pdf.save(`elemzes-${jobId}.pdf`);
    } catch (error) {
      console.error('PDF generálási hiba:', error);
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <Box sx={{ mt: 4, p: 3, position: 'relative' }}>
      {/* PDF gomb – spinnerrel generálás közben */}
      {/* PDF gomb – reszponzív elhelyezés */}
<Box
  sx={{
    // Mobilon: normál blokk, ne legyen abszolút
    position: { xs: 'static', md: 'absolute' },
    top: { md: 16 },
    right: { md: 16 },
    // Adjunk kis helyet a következő elem előtt mobilon
    mb: { xs: 2, md: 0 },
    // Igazítás: mobilon teljes szélesség, desktopon jobb szélre
    display: 'flex',
    justifyContent: { xs: 'stretch', md: 'flex-end' },
    width: { xs: '100%', md: 'auto' },
    zIndex: 1,
  }}
>
  <Button
    variant="contained"
    onClick={handleDownloadPdf}
    size={pdfGenerating ? 'small' : 'medium'}
    disabled={pdfGenerating}
    startIcon={pdfGenerating ? <CircularProgress size={16} thickness={5} /> : <DownloadIcon />}
    // Mobilon legyen széles gomb
    fullWidth
  >
    {pdfGenerating ? 'PDF készítése…' : 'PDF letöltése'}
  </Button>
</Box>


      {/* Fejléc */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6">
          {propJobId ? 'Feldolgozás állapota:' : 'Elemzés részletei'} {status}
        </Typography>
        <Chip label={status || 'N/A'} color={chipColor as any} />
        {averageScore !== null && (
          <Chip label={`Átlag: ${averageScore.toFixed(2)}`} color="primary" variant="outlined" />
        )}
      </Box>

      {comments.length > 0 && (
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          textColor="primary"
          indicatorColor="primary"
          centered
        >
          <Tab icon={<ChatBubbleOutlineIcon />} label="Kommentek" />
          <Tab icon={<BarChartIcon />} label="Statisztika" />
        </Tabs>
      )}

      {loading && <CircularProgress sx={{ mt: 2 }} />}

      {!loading && (
        <>
          {tabIndex === 0 && <JobResultComments comments={comments} status={status} />}
          {tabIndex === 1 && <JobResultStatistics comments={comments} />}
        </>
      )}

      {/* Rejtett riport – teljesen off-screen, NINCS globális hatás */}
      <Box
        ref={reportRef}
        sx={{
          position: 'fixed',
          left: -10000,
          top: 0,
          width: 1080,
          bgcolor: '#fff',
          zIndex: -1,
        }}
      >
        {ReportContent}
      </Box>
    </Box>
  );
}
